<script lang="ts">
    import { goto } from '$app/navigation';
    import { t } from '$lib/i18n';
    import { formatNumber, formatViewCount } from '$lib/utils/numberUtils';
    
    // shadcn/svelteコンポーネント
    import { Button } from "$lib/components/shadcn/ui/button";
    import { Badge } from "$lib/components/shadcn/ui/badge";
    import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "$lib/components/shadcn/ui/dropdown-menu";
    
    import { 
        ArrowLeft, 
        Info,
        MessageSquare,
        Youtube,
        MoreVertical,
        RefreshCw
    } from 'lucide-svelte';

    // Props
    export let videoDetails: any;
    export let channelData: any;
    export let totalSuperChatJpy: number = 0;
    export let totalCommentCount: number = 0;
    export let concurrentViewers: number = 0;
    export let maxConcurrentViewers: number = 0;
    export let currentMode: string;
    export let handleBackClick: () => void;
    export let switchMode: (mode: string) => void;
    
    // モードの定数
    const MODES = {
        CHAT: 'chat',
        DETAILS: 'details',
        RECENT: 'recent'
    };
</script>

<div class="flex items-center justify-between border-b border-border">
    <Button 
        variant="ghost"
        size="icon"
        on:click={handleBackClick}
        class="h-10 w-10 relative z-10"
    >
        <ArrowLeft size={20} />
        <span class="sr-only">戻る</span>
    </Button>

    <div class="flex items-center space-x-2 ml-auto pr-3">
        {#if videoDetails.live_broadcast_content === 'live' || videoDetails.live_broadcast_content === 'none'}
            {#if totalCommentCount > 0}
                <Badge variant="outline" class="flex items-center space-x-1 px-2 py-1 bg-yellow-50 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800/50">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div class="text-sm">
                        {formatNumber(totalSuperChatJpy)}
                    </div>
                </Badge>
                <Badge variant="outline" class="flex items-center space-x-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/50">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <div class="text-sm">
                        {totalCommentCount || 0}
                    </div>
                </Badge>
            {/if}
            
            <Badge variant="outline" class="flex items-center gap-1 bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800/50 py-1 px-2 text-[13px]">
                {#if videoDetails.live_broadcast_content === 'live'}
                    <span class="w-1 h-1 rounded-full bg-red-500"></span>
                    <span class="font-medium">{formatViewCount(concurrentViewers)}</span>
                    <span>{t('video_page.watching_now', "watch")}</span>
                {:else if videoDetails.live_broadcast_content === 'none'}
                    <span class="font-medium">{formatViewCount(maxConcurrentViewers)}</span>
                    <span class="text-xs">最高同時視聴</span>
                {/if}
            </Badge>
        {/if}
    </div>

    <div class="flex pr-2">
        {#if videoDetails.live_broadcast_content === 'live'}
            <Button 
                variant={currentMode === MODES.CHAT ? "default" : "ghost"}
                size="sm"
                on:click={() => switchMode(MODES.CHAT)}
                class="px-2 h-8 dark:text-white"
            >
                <MessageSquare size={14} class="mr-1" />
                <span class="text-xs font-medium">{t('video_page.chat', 'Chat')}</span>
            </Button>
        {/if}
        <Button 
            variant={currentMode === MODES.DETAILS ? "default" : "ghost"}
            size="sm"
            on:click={() => switchMode(MODES.DETAILS)}
            class="px-2 h-8 dark:text-white"
        >
            <Info size={14} class="mr-1" />
            <span class="text-xs font-medium">{t('video_page.details', '詳細')}</span>
        </Button>
        <Button 
            variant={currentMode === MODES.RECENT ? "default" : "ghost"}
            size="sm"
            on:click={() => switchMode(MODES.RECENT)}
            class="px-2 h-8 dark:text-white"
        >
            <Youtube size={14} class="mr-1" />
            <span class="text-xs font-medium">{t('video_page.video_new', '新着')}</span>
        </Button>
        
        <!-- 三点リーダーメニュー -->
        <DropdownMenu>
            <DropdownMenuTrigger>
                <Button 
                    variant="ghost"
                    size="sm"
                    class="px-2 h-8 dark:text-white"
                >
                    <MoreVertical size={16} />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="w-48 z-[100]">
                <DropdownMenuItem on:click={() => {
                    const youtubeUrl = `https://www.youtube.com/watch?v=${videoDetails.id}`;
                    window.open(youtubeUrl, '_blank');
                }}>
                    <div class="flex items-center gap-2 w-full">
                        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z" fill="#FF0000"/>
                            <path d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FFFFFF"/>
                        </svg>
                        <span>YouTubeで開く</span>
                    </div>
                </DropdownMenuItem>
                <DropdownMenuItem on:click={() => {
                    window.location.reload();
                }}>
                    <div class="flex items-center gap-2 w-full">
                        <RefreshCw size={15} />
                        <span>ページを再読み込み</span>
                    </div>
                </DropdownMenuItem>
                <DropdownMenuItem on:click={() => {
                    goto('/');
                }}>
                    <div class="flex items-center gap-2 w-full">
                        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                            <polyline points="9 22 9 12 15 12 15 22"></polyline>
                        </svg>
                        <span>トップページへ</span>
                    </div>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    </div>
</div> 